import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSnackbar } from "src/components/Snackbar/useSnackbar";
import {
  deleteTrainingQuiz,
  getTraineeTrainingQuiz,
  getTrainingQuiz,
  publishTrainingQuiz,
  submitTrainingQuiz,
  TrainingQuiz,
  TrainingQuizState,
} from "src/services/backend";
import { invalidateProgramAndTasks } from "./task-program-mutations";

const trainingQuizQueryKey = (
  orgId: string,
  programId: string,
  documentVersionId: string
) => ["trainingQuiz", orgId, programId, documentVersionId] as const;

export const invalidateTrainingQuiz = (
  queryClient: QueryClient,
  orgId: string,
  programId: string,
  documentVersionId: string
) =>
  queryClient.invalidateQueries({
    queryKey: trainingQuizQueryKey(orgId, programId, documentVersionId),
  });

const traineeTrainingQuizQueryKey = (orgId: string, taskStateId: string) =>
  ["traineeTrainingQuiz", orgId, taskStateId] as const;

export const useGetTrainingQuiz = ({
  orgId,
  programId,
  documentVersionId,
  enabled = true,
}: {
  orgId: string;
  programId: string;
  documentVersionId: string;
  enabled?: boolean;
}) =>
  useQuery<TrainingQuiz | null>({
    queryKey: trainingQuizQueryKey(orgId, programId, documentVersionId),
    queryFn: async () => {
      const { data } = await getTrainingQuiz(
        orgId,
        programId,
        documentVersionId
      );
      return data;
    },
    enabled: enabled && !!orgId && !!programId && !!documentVersionId,
  });

export const useDeleteTrainingQuiz = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async ({
      programId,
      documentVersionId,
      expectedUpdatedAt,
      expectedState,
      confirmedCompletedTaskIds,
    }: {
      programId: string;
      documentVersionId: string;
      expectedUpdatedAt: string;
      expectedState: TrainingQuizState;
      confirmedCompletedTaskIds: string[];
    }) => {
      const { data } = await deleteTrainingQuiz(
        orgId,
        programId,
        documentVersionId,
        expectedUpdatedAt,
        expectedState,
        confirmedCompletedTaskIds
      );
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        trainingQuizQueryKey(
          orgId,
          variables.programId,
          variables.documentVersionId
        ),
        null
      );
      showSnackbar(
        data.deletedTaskCount > 0
          ? `Quiz deleted and ${data.deletedTaskCount} training task${data.deletedTaskCount === 1 ? "" : "s"} removed`
          : "Quiz deleted"
      );
    },
    onError: (error) => {
      showSnackbar(
        error instanceof Error ? error.message : "Could not delete quiz"
      );
    },
    onSettled: (_data, _error, variables) => {
      invalidateTrainingQuiz(
        queryClient,
        orgId,
        variables.programId,
        variables.documentVersionId
      );
      invalidateProgramAndTasks(queryClient, orgId, variables.programId);
    },
  });
};

export const usePublishTrainingQuiz = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      programId,
      documentVersionId,
    }: {
      programId: string;
      documentVersionId: string;
    }) => {
      const { data } = await publishTrainingQuiz(
        orgId,
        programId,
        documentVersionId
      );
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        trainingQuizQueryKey(
          orgId,
          variables.programId,
          variables.documentVersionId
        ),
        data.quiz
      );
      showSnackbar(
        data.createdTaskCount > 0
          ? `Quiz published and ${data.createdTaskCount} training task${data.createdTaskCount === 1 ? "" : "s"} sent`
          : "Quiz published"
      );
    },
    onError: (error) => {
      showSnackbar(
        error instanceof Error ? error.message : "Could not publish quiz"
      );
    },
    onSettled: (_data, _error, variables) => {
      invalidateTrainingQuiz(
        queryClient,
        orgId,
        variables.programId,
        variables.documentVersionId
      );
      invalidateProgramAndTasks(queryClient, orgId, variables.programId);
    },
  });
};

export const useGetTraineeTrainingQuiz = ({
  orgId,
  taskStateId,
}: {
  orgId: string;
  taskStateId: string;
}) =>
  useQuery({
    queryKey: traineeTrainingQuizQueryKey(orgId, taskStateId),
    queryFn: async () => {
      const { data } = await getTraineeTrainingQuiz(orgId, taskStateId);
      return data;
    },
    enabled: !!orgId && !!taskStateId,
  });

export const useSubmitTrainingQuiz = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async ({
      taskStateId,
      answers,
    }: {
      taskStateId: string;
      answers: Array<{ questionId: string; optionId: string }>;
    }) => {
      const { data } = await submitTrainingQuiz(orgId, taskStateId, answers);
      return data;
    },
    onError: (error) => {
      showSnackbar(
        error instanceof Error ? error.message : "Could not submit quiz"
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: traineeTrainingQuizQueryKey(orgId, variables.taskStateId),
      });
      invalidateProgramAndTasks(queryClient, orgId);
    },
  });
};
